

import React, { useEffect, useState, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Type definitions
interface UserParam {
  TOKEN?: string;
  USERREFCODE?: string;
  USERNAME?: string;
  COMPANYLOGO?: string;
  COMPCODE?: string;
  COMPNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParam;
  TOKEN?: string;
  USERREFCODE?: string;
}

interface RootState {
  user: UserState;
  accessRights: Record<string, unknown>;
}

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[] | null;
  redirectTo?: string;
}

/**
 * ProtectedRoute Component
 *
 * @param children - The component to render if authenticated
 * @param allowedRoles - Optional array of allowed roles (for future RBAC)
 * @param redirectTo - Path to redirect to if not authenticated (default: '/')
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = null,
  redirectTo = '/'
}) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);

  // Get user data from Redux store
  const userState = useSelector((state: RootState) => state.user);
  const accessRights = useSelector((state: RootState) => state.accessRights);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if user data exists and has a token
        const userParam = userState?.USER_PARAM;
        const hasToken = userParam?.TOKEN || userState?.TOKEN;
        const hasUserRefCode = userParam?.USERREFCODE || userState?.USERREFCODE;

        // Also check localStorage as a fallback (for redux-persist rehydration)
        let tokenFromStorage: string | null = null;
        try {
          const userPersist = localStorage.getItem('persist:user');
          if (userPersist) {
            const parsedUser = JSON.parse(userPersist);
            if (parsedUser.USER_PARAM) {
              const userParamFromStorage = JSON.parse(parsedUser.USER_PARAM);
              tokenFromStorage = userParamFromStorage?.TOKEN;
            }
          }
        } catch (error) {
          console.debug('Error checking localStorage for token:', error);
        }

        // User is authenticated if they have a token (from Redux or localStorage)
        const authenticated = !!(hasToken || tokenFromStorage) && !!hasUserRefCode;

        // If authenticated, check role-based access (if roles are specified)
        if (authenticated && allowedRoles && allowedRoles.length > 0) {
          // Future: Implement role checking logic here
          // For now, if roles are specified but not implemented, allow access
          // This can be enhanced based on your RBAC implementation
        }

        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuthentication();
  }, [userState, accessRights, allowedRoles]);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location so we can redirect back after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;
